import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useLaptopMobileTextData } from './hooks';

import { RichText } from 'components/Prismic';
import { Normalize } from 'utils/types';

export type TLaptopMobileTextSection =
  Normalize<Queries.SolutionDesktopLaptopMobileTextData_PrismicSolutionDataDesktopSolutionLaptopMobileText_Fragment>;

const Container = styled.div`
  ${({ theme: { colors } }) => css`
    display: grid;
    position: relative;
    align-content: flex-start;
    justify-content: center;
    background-color: ${colors.backgroundDark};
    padding-bottom: 9.625rem;
    overflow: hidden;
  `}
`;

const LaptopContainer = styled.div`
  display: grid;
`;

const LaptopImage = styled(GatsbyImage)`
  grid-row: 1 / span 1;
  grid-column: 1 / span 1;
  width: 173.75rem;
`;

const LaptopTitle = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    flex-direction: column;
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;
    z-index: 1;

    & > h2 {
      margin-top: 24.625rem;
      text-align: center;
      line-height: 1.04;
      letter-spacing: -0.1em;
      color: ${colors.silverGray};
      font-size: 10rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};

      strong {
        letter-spacing: -0.1em;
        font-size: 12rem;

        ${tp.useFont(fonts => fonts.Switzer.bold)};
      }
    }
  `}
`;

const MobileContainer = styled.div`
  display: grid;
  grid-row: 1 / span 1;
  grid-column: 1 / span 1;
  align-items: center;
  align-self: flex-end;
  justify-self: flex-end;
  margin-bottom: 3rem;
`;

const MobileImage = styled(GatsbyImage)`
  grid-row: 1 / span 1;
  grid-column: 1 / span 1;
  align-self: flex-end;
  width: 32.25rem;
`;

const MobileTitle = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    flex-direction: column;
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;
    z-index: 1;

    & > h3 {
      text-align: center;
      line-height: 1.04;
      letter-spacing: -0.1em;
      color: ${colors.silverGray};
      font-size: 4.75rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};

      strong {
        letter-spacing: -0.11em;
        font-size: 5.6875rem;
        ${tp.useFont(fonts => fonts.Switzer.bold)};
      }
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    position: relative;
    justify-self: center;
    z-index: 1;
    border-radius: 3.75rem;
    background-color: ${colors.black}73;
    width: fit-content;

    & > p {
      padding: 2.5rem 3.75rem;
      text-align: center;
      line-height: 1.2;
      color: ${colors.primary};
      font-size: 3.25rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }
  `}
`;

const LaptopMobileTextSection: React.FC<TLaptopMobileTextSection> = sliceData => {
  const { laptopImage, mobileImage, laptopTitle, mobileTitle, description } =
    useLaptopMobileTextData(sliceData);

  return (
    <Container>
      <LaptopContainer>
        {laptopImage ? <LaptopImage {...laptopImage} /> : null}
        <LaptopTitle render={laptopTitle} />
        {mobileImage ? (
          <MobileContainer>
            <MobileImage {...mobileImage} />
            <MobileTitle render={mobileTitle} />
          </MobileContainer>
        ) : null}
      </LaptopContainer>
      <Description render={description} />
    </Container>
  );
};

export const desktopLaptopMobileTexFragment = graphql`
  fragment SolutionDesktopLaptopMobileTextData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionLaptopMobileText {
      slice_type

      primary {
        solution_laptop_mobile_text_laptop_image {
          gatsbyImageData
          alt
        }
        solution_laptop_mobile_text_mobile_image {
          gatsbyImageData
          alt
        }
        solution_laptop_mobile_text_laptop_title {
          richText
        }
        solution_laptop_mobile_text_mobile_title {
          richText
        }
        solution_laptop_mobile_text_description {
          richText
        }
      }
    }
  }
`;

export default LaptopMobileTextSection;
